import React, { useState } from 'react';
import { ClinicFormProps } from '../../../CreateClinic/types';
import ClinicConditionViewer from './ClinicConditionViewer';
import { ClinicCondition } from '../../../types';
import useSetEditedCondition from './useSetEditedCondition';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { HealtheraButton, SecondaryButton } from '../../../../../healtheraComponents';
import useDeleteCondition from '../../hooks/useDeleteCondition';

interface ConditionViewerEditorsListProps extends ClinicFormProps {
  clinic: {
    _id: string;
  };
}

function ConditionViewersList({ form, clinic }: ConditionViewerEditorsListProps) {
  const conditions = form.watch('clinic_conditions');
  const [conditionToDelete, setConditionToDelete] = useState<string | null>(null);

  const setEditedCondition = useSetEditedCondition();
  const { deleteCondition, isDeleting } = useDeleteCondition({ form, clinicId: clinic._id });
  const hasValidationErrors = useHasValidationErrors(form);

  const handleConfirmDelete = () => {
    if (conditionToDelete) {
      deleteCondition(conditionToDelete);
      setConditionToDelete(null);
    }
  };

  const renderCondition = (condition: ClinicCondition) => {
    const handleEditClick = () => {
      setEditedCondition(condition._id);
    };

    const hasError = hasValidationErrors(condition._id);

    return (
      <ClinicConditionViewer
        hasError={hasError}
        onDeleteClick={() => setConditionToDelete(condition._id)}
        onEditClick={handleEditClick}
        clinicCondition={condition}
        currentClinicId={clinic._id}
      />
    );
  };

  return (
    <>
      {conditions.map((condition) => (
        <div key={condition._id}>
          {renderCondition(condition)}
        </div>
      ))}
      <Dialog
        open={Boolean(conditionToDelete)}
        onClose={() => !isDeleting && setConditionToDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Condition</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this condition?
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setConditionToDelete(null)} disabled={isDeleting}>
            Cancel
          </SecondaryButton>
          <HealtheraButton onClick={handleConfirmDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </HealtheraButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

const useHasValidationErrors = (form: ClinicFormProps['form']) => {
  function hasValidationErrors(clinicConditionId: string) {
    const conditions = form.getValues('clinic_conditions');
    const conditionIndex = conditions.findIndex(condition => condition._id === clinicConditionId);

    if (conditionIndex === -1) {
      return false;
    }

    const conditionError = form.formState.errors.clinic_conditions?.[conditionIndex];

    return Boolean(conditionError);
  }
  return hasValidationErrors;
};

export default ConditionViewersList;
